import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, PlusCircle, LogOut, Copy, Check, Baby, Mail, Phone, 
  Send, Loader2, Zap, Moon, Sun, Clock, Heart, Utensils, Sparkles, Star, Cloud,
  Plus, Trash2, Save, ArrowLeft, Eye, Share2, ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import Logo from '../../components/common/Logo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import apiService from '../../services/api';

// ==================== TEMPLATES POR FAIXA ETÁRIA ====================

const AGE_RANGES = [
  { id: '0-3', label: '0 a 3 meses', minAge: 0, maxAge: 3 },
  { id: '4-6', label: '4 a 6 meses', minAge: 4, maxAge: 6 },
  { id: '7-9', label: '7 a 9 meses', minAge: 7, maxAge: 9 },
  { id: '10-12', label: '10 a 12 meses', minAge: 10, maxAge: 12 },
  { id: '13-24', label: '1 a 2 anos', minAge: 13, maxAge: 24 },
];

const getAgeRangeFromAge = (age) => {
  for (const range of AGE_RANGES) {
    if (age >= range.minAge && age <= range.maxAge) {
      return range.id;
    }
  }
  return '4-6'; // default
};

// Template para 0-3 meses
const getTemplate0to3 = (babyName, babyAge) => ({
  baby_name: babyName,
  baby_age: babyAge,
  summary: `${babyName} tem ${babyAge} ${babyAge === 1 ? 'mês' : 'meses'} e está na fase de recém-nascido, onde o sono ainda é bastante irregular e fragmentado. Isso é completamente normal! Nessa idade, o bebê ainda não tem o ciclo circadiano desenvolvido e precisa mamar com frequência. O foco principal é criar uma base de segurança e começar a estabelecer pequenas rotinas que ajudarão no futuro. Não há expectativa de "treino de sono" nessa fase - apenas acolhimento e previsibilidade.`,
  pillars: [
    {
      id: 1,
      icon: 'Sun',
      color: '#F5A623',
      title: 'Diferenciação dia e noite',
      mainValue: '',
      instructions: [
        'Durante o dia: ambiente claro, barulhos normais da casa, interação durante mamadas',
        'À noite: luz baixa, silêncio, mamadas calmas e sem estímulos',
        'Expor o bebê à luz natural pela manhã (mesmo que indiretamente)',
        'Evitar luzes fortes à noite, especialmente luz azul de telas'
      ],
      note: 'A partir das 6-8 semanas o bebê começa a diferenciar dia e noite. Ajude esse processo!'
    },
    {
      id: 2,
      icon: 'Clock',
      color: '#8F7AC5',
      title: 'Janelas de sono curtas',
      mainValue: '45min - 1h30',
      instructions: [
        'Recém-nascidos ficam acordados por períodos muito curtos',
        '0-6 semanas: 45 minutos a 1 hora de janela',
        '6-12 semanas: 1 hora a 1h30 de janela',
        'Observar sinais de sono: olhar vidrado, movimentos lentos, bocejos'
      ],
      note: 'Bebês dessa idade dormem 16-18 horas por dia, distribuídas em vários períodos.'
    },
    {
      id: 3,
      icon: 'Moon',
      color: '#30244F',
      title: 'Ambiente acolhedor para dormir',
      mainValue: '',
      instructions: [
        'Swaddle (charutinho) ajuda a acalmar o reflexo de Moro',
        'Ruído branco contínuo imita sons do útero',
        'Quarto escuro para sonecas e noite',
        'Temperatura entre 20-22°C',
        'Movimentos rítmicos (balanço, colo) ajudam a acalmar'
      ],
      note: 'O bebê ainda está se adaptando ao mundo fora do útero. Recrie sensações familiares!'
    },
    {
      id: 4,
      icon: 'Utensils',
      color: '#D98DAE',
      title: 'Alimentação em livre demanda',
      mainValue: '',
      meals: [
        'Amamentação ou fórmula em livre demanda',
        'Intervalos de 2-3 horas durante o dia são comuns',
        'À noite, pode haver um período maior de sono (3-4h)',
        'Não há expectativa de "dormir a noite toda" nessa fase'
      ],
      note: 'O estômago do bebê é pequeno e precisa de alimentação frequente. Isso é normal e esperado.'
    },
    {
      id: 5,
      icon: 'Heart',
      color: '#7BC47F',
      title: 'Vínculo e segurança',
      mainValue: '',
      instructions: [
        'Atenda prontamente ao choro - bebês não se "acostumam mal"',
        'Contato pele a pele fortalece o vínculo',
        'Colo e aconchego são necessidades básicas nessa fase',
        'Não se preocupe em criar "maus hábitos" - o bebê precisa de você'
      ],
      note: 'Bebês que se sentem seguros dormem melhor. Priorize o vínculo!'
    }
  ],
  routine: [
    { time: '07:00', activity: 'Acordar + mamada + troca' },
    { time: '08:00', activity: '1ª soneca (pode ser no colo ou sling)' },
    { time: '09:30', activity: 'Acordar + mamada' },
    { time: '10:30', activity: '2ª soneca' },
    { time: '12:00', activity: 'Acordar + mamada' },
    { time: '13:00', activity: '3ª soneca' },
    { time: '14:30', activity: 'Acordar + mamada' },
    { time: '15:30', activity: '4ª soneca' },
    { time: '17:00', activity: 'Acordar + mamada' },
    { time: '18:00', activity: '5ª soneca curta (30-45min)' },
    { time: '18:45', activity: 'Banho relaxante' },
    { time: '19:15', activity: 'Mamada + rotina calma' },
    { time: '19:30', activity: 'Dormir (com despertares para mamar)' }
  ],
  priorities: {
    'Semana 1-2 - Observação': [
      'Observar padrões naturais do bebê sem tentar mudar nada',
      'Anotar horários de sono e mamadas',
      'Cuidar da sua saúde e descanso - durma quando o bebê dormir',
      'Não se cobrar demais - essa fase é de adaptação'
    ],
    'Semana 3-4 - Pequenos ajustes': [
      'Começar a diferenciar dia e noite com luz e ambiente',
      'Observar janelas de sono e colocar para dormir antes de ficar irritado',
      'Introduzir ruído branco se ainda não usa',
      'Manter expectativas realistas para a idade'
    ]
  },
  expectations: [
    { period: 'Semana 1-2', desc: 'Fase de conhecer o bebê. Sono irregular é completamente normal.' },
    { period: 'Semana 3-4', desc: 'Começam pequenos padrões. O bebê pode ter um período maior de sono à noite.' }
  ],
  finalMessage: `Mamãe de ${babyName}, essa fase é intensa mas passa rápido! Lembre-se: seu bebê acabou de chegar ao mundo e precisa de muito acolhimento. Não existe "mimar demais" um recém-nascido. Cuide de você também - aceite ajuda, descanse quando puder e confie nos seus instintos. Você está fazendo um trabalho incrível! 💜`
});

// Template para 4-6 meses
const getTemplate4to6 = (babyName, babyAge) => ({
  baby_name: babyName,
  baby_age: babyAge,
  summary: `${babyName} tem ${babyAge} meses e está em uma fase de grande desenvolvimento! Nessa idade, o sono começa a se organizar e o bebê já consegue fazer períodos maiores à noite. É também quando muitos bebês passam pela famosa "regressão dos 4 meses", que na verdade é uma progressão - o sono está amadurecendo. O objetivo é aproveitar essa janela para criar bons hábitos de sono, sempre respeitando o ritmo de ${babyName}.`,
  pillars: [
    {
      id: 1,
      icon: 'Sun',
      color: '#F5A623',
      title: 'Horário consistente para iniciar o dia',
      mainValue: '07:00',
      instructions: [
        'Acordar no mesmo horário todos os dias, mesmo que a noite tenha sido difícil',
        'Abrir as cortinas e expor à luz natural logo cedo',
        'Criar uma rotina matinal calma e previsível',
        'Primeira mamada do dia em ambiente claro'
      ],
      note: 'A consistência no horário de acordar ajuda a regular o relógio biológico do bebê.'
    },
    {
      id: 2,
      icon: 'Clock',
      color: '#8F7AC5',
      title: 'Janelas de sono adequadas',
      mainValue: '1h45 - 2h30',
      instructions: [
        'Primeira janela do dia: 1h45 - 2h após acordar',
        'Segunda e terceira janelas: 2h - 2h30',
        'Última janela antes de dormir: 2h30 - 3h',
        'Observar sinais de sono: bocejos, esfregar olhos, irritabilidade',
        '3 sonecas por dia é o padrão para essa idade'
      ],
      note: 'Bebês de 4-6 meses geralmente fazem 3 sonecas, totalizando 3-4 horas de sono diurno.'
    },
    {
      id: 3,
      icon: 'Moon',
      color: '#30244F',
      title: 'Rotina noturna consistente',
      mainValue: '18:30 - 19:30',
      instructions: [
        'Iniciar rotina 30-45 minutos antes do horário de dormir',
        'Banho relaxante em temperatura morna',
        'Massagem suave com movimentos lentos',
        'Alimentação em ambiente calmo e com pouca luz',
        'Canção de ninar ou história curta',
        'Colocar no berço sonolento, mas acordado'
      ],
      note: 'A previsibilidade da rotina sinaliza ao bebê que está chegando a hora de dormir.'
    },
    {
      id: 4,
      icon: 'Utensils',
      color: '#D98DAE',
      title: 'Alimentação adequada',
      mainValue: '',
      meals: [
        '07:00 - Mamada/mamadeira ao acordar',
        '09:30 - Mamada (após primeira soneca)',
        '11:30 - Mamada',
        '14:00 - Mamada (após segunda soneca)',
        '16:30 - Mamada',
        '18:30 - Última mamada antes de dormir',
        'Introdução alimentar: se já iniciou, 1 refeição por dia'
      ],
      note: 'Garantir que o bebê esteja bem alimentado durante o dia reduz despertares por fome à noite.'
    },
    {
      id: 5,
      icon: 'Heart',
      color: '#7BC47F',
      title: 'Ambiente de sono ideal',
      mainValue: '',
      instructions: [
        'Quarto escuro (usar blackout se necessário)',
        'Temperatura entre 20-22°C',
        'Ruído branco constante durante todo o sono',
        'Roupas confortáveis - saco de dormir é uma ótima opção',
        'Berço livre de objetos soltos'
      ],
      note: 'O ambiente adequado facilita a transição entre ciclos de sono.'
    }
  ],
  routine: [
    { time: '07:00', activity: 'Acordar + mamada' },
    { time: '08:45', activity: '1ª soneca (1h - 1h30)' },
    { time: '10:15', activity: 'Acordar + mamada' },
    { time: '12:15', activity: '2ª soneca (1h - 1h30)' },
    { time: '14:00', activity: 'Acordar + mamada' },
    { time: '16:30', activity: '3ª soneca curta (30-45min)' },
    { time: '17:15', activity: 'Acordar + mamada' },
    { time: '18:00', activity: 'Início da rotina noturna' },
    { time: '18:30', activity: 'Última mamada' },
    { time: '19:00', activity: 'Dormir' }
  ],
  priorities: {
    'Semana 1 - Preparação': [
      'Estabelecer horário fixo de acordar',
      'Criar rotina noturna consistente',
      'Ajustar ambiente de sono (blackout, ruído branco)',
      'Observar e anotar padrões de sono atuais'
    ],
    'Semana 2 - Ajustes': [
      'Ajustar janelas de sono conforme resposta do bebê',
      'Começar a colocar no berço mais sonolento',
      'Trabalhar na primeira soneca do dia no berço',
      'Manter consistência na rotina'
    ],
    'Semana 3 - Consolidação': [
      'Fortalecer habilidade de adormecer com menos intervenção',
      'Estender sonecas gradualmente',
      'Reduzir mamadas noturnas se apropriado',
      'Celebrar pequenas vitórias!'
    ],
    'Semana 4 - Manutenção': [
      'Manter rotina estabelecida',
      'Lidar com regressões com paciência',
      'Ajustar conforme desenvolvimento do bebê',
      'Avaliar progresso e próximos passos'
    ]
  },
  expectations: [
    { period: 'Semana 1-2', desc: 'Adaptação às mudanças. Possível resistência inicial é normal.' },
    { period: 'Semana 2-3', desc: 'Começam as melhorias. Sono mais organizado.' },
    { period: 'Semana 3-4', desc: 'Consolidação. Bebê adormece com mais facilidade.' }
  ],
  finalMessage: `Lembre-se: cada bebê é único e tem seu próprio ritmo. ${babyName} pode precisar de mais ou menos tempo para se adaptar, e isso é completamente normal. Confie no processo, mantenha a consistência e celebre cada pequena conquista. Você está fazendo um trabalho incrível! 💜`
});

// Template para 7-9 meses
const getTemplate7to9 = (babyName, babyAge) => ({
  baby_name: babyName,
  baby_age: babyAge,
  summary: `${babyName} tem ${babyAge} meses e está em uma fase muito ativa! Nessa idade, o bebê está desenvolvendo muitas habilidades motoras (sentar, engatinhar, ficar de pé) e isso pode afetar o sono temporariamente. Também é comum a ansiedade de separação começar a aparecer. O foco é manter a consistência, adaptar a rotina para 2-3 sonecas e garantir que ${babyName} tenha oportunidades para praticar suas novas habilidades durante o dia.`,
  pillars: [
    {
      id: 1,
      icon: 'Sun',
      color: '#F5A623',
      title: 'Horário consistente',
      mainValue: '06:30 - 07:00',
      instructions: [
        'Acordar no mesmo horário todos os dias',
        'Luz natural logo cedo ajuda a regular o ritmo',
        'Primeira mamada do dia seguida de café da manhã',
        'Atividades estimulantes pela manhã'
      ],
      note: 'A regularidade é ainda mais importante nessa fase de muitas mudanças!'
    },
    {
      id: 2,
      icon: 'Clock',
      color: '#8F7AC5',
      title: 'Transição para 2 sonecas',
      mainValue: '2h30 - 3h30',
      instructions: [
        'Primeira janela: 2h30 - 3h',
        'Segunda janela: 3h - 3h30',
        'Última janela (antes de dormir): 3h30 - 4h',
        'Alguns bebês ainda fazem 3 sonecas - respeitar o ritmo',
        'Transição para 2 sonecas geralmente acontece entre 7-9 meses'
      ],
      note: 'Se o bebê resistir à terceira soneca ou ela atrapalhar a noite, é hora de transicionar.'
    },
    {
      id: 3,
      icon: 'Moon',
      color: '#30244F',
      title: 'Rotina noturna previsível',
      mainValue: '18:30 - 19:00',
      instructions: [
        'Jantar em família quando possível',
        'Brincadeira calma após jantar',
        'Banho relaxante',
        'Mamada/mamadeira em ambiente calmo',
        'Livro ou canção de ninar',
        'Momento de conexão antes de dormir ajuda na ansiedade de separação'
      ],
      note: 'Despedida carinhosa e consistente ajuda o bebê a se sentir seguro.'
    },
    {
      id: 4,
      icon: 'Utensils',
      color: '#D98DAE',
      title: 'Alimentação completa',
      mainValue: '3 refeições + leite',
      meals: [
        '07:00 - Mamada + café da manhã',
        '09:30 - Lanche (fruta)',
        '12:00 - Almoço completo',
        '15:00 - Lanche + mamada',
        '18:00 - Jantar',
        '18:45 - Última mamada antes de dormir'
      ],
      note: 'Alimentação sólida bem estabelecida ajuda a reduzir despertares noturnos por fome.'
    },
    {
      id: 5,
      icon: 'Heart',
      color: '#7BC47F',
      title: 'Gerenciando a ansiedade de separação',
      mainValue: '',
      instructions: [
        'Brincar de esconde-esconde durante o dia',
        'Praticar separações curtas e voltar',
        'Despedida consistente e confiante na hora de dormir',
        'Objeto de transição pode ajudar (naninha, paninho)',
        'Não "fugir" enquanto o bebê não está olhando'
      ],
      note: 'A ansiedade de separação é uma fase normal do desenvolvimento. Passa!'
    }
  ],
  routine: [
    { time: '06:30', activity: 'Acordar + mamada' },
    { time: '07:00', activity: 'Café da manhã' },
    { time: '09:00', activity: '1ª soneca (1h - 1h30)' },
    { time: '10:30', activity: 'Acordar + lanche' },
    { time: '12:00', activity: 'Almoço' },
    { time: '13:30', activity: '2ª soneca (1h30 - 2h)' },
    { time: '15:30', activity: 'Acordar + lanche + mamada' },
    { time: '17:30', activity: 'Jantar' },
    { time: '18:15', activity: 'Início da rotina noturna' },
    { time: '18:45', activity: 'Última mamada' },
    { time: '19:00', activity: 'Dormir' }
  ],
  priorities: {
    'Semana 1 - Avaliação': [
      'Avaliar se é hora de transicionar para 2 sonecas',
      'Garantir oportunidades para praticar habilidades motoras durante o dia',
      'Estabelecer rotina de refeições sólidas',
      'Reforçar rotina noturna consistente'
    ],
    'Semana 2 - Ajustes': [
      'Ajustar janelas de sono para a nova configuração',
      'Trabalhar na ansiedade de separação com despedidas consistentes',
      'Garantir que as sonecas sejam restauradoras',
      'Manter ambiente escuro e tranquilo'
    ],
    'Semana 3-4 - Consolidação': [
      'Manter consistência mesmo com marcos de desenvolvimento',
      'Ajustar conforme necessário',
      'Celebrar o progresso',
      'Preparar para próxima fase'
    ]
  },
  expectations: [
    { period: 'Semana 1-2', desc: 'Ajustes na rotina. Possíveis despertares por marcos de desenvolvimento.' },
    { period: 'Semana 3-4', desc: 'Estabilização. Bebê adaptado à nova rotina com 2 sonecas.' }
  ],
  finalMessage: `${babyName} está crescendo tão rápido! Essa fase de tantas conquistas é incrível de acompanhar. Lembre-se que oscilações no sono são normais quando o bebê está aprendendo coisas novas. Mantenha a calma, a consistência e confie que logo tudo se estabiliza. Você está arrasando! 💜`
});

// Template para 10-12 meses
const getTemplate10to12 = (babyName, babyAge) => ({
  baby_name: babyName,
  baby_age: babyAge,
  summary: `${babyName} tem ${babyAge} meses e está quase completando o primeiro ano! Nessa fase, o bebê geralmente já está bem estabelecido em 2 sonecas e pode dormir períodos longos à noite. Os desafios comuns incluem a "regressão dos 12 meses" (associada ao aprendizado de andar) e resistência às sonecas. O foco é manter a consistência, garantir que o bebê não fique muito cansado e celebrar todo o progresso feito até aqui!`,
  pillars: [
    {
      id: 1,
      icon: 'Sun',
      color: '#F5A623',
      title: 'Início do dia consistente',
      mainValue: '06:30 - 07:00',
      instructions: [
        'Acordar natural entre 6:30 e 7:00',
        'Antes das 6:00 ainda é considerado noite - tratar como despertar noturno',
        'Luz natural e café da manhã logo após acordar',
        'Atividades físicas pela manhã (praticar andar, brincar no chão)'
      ],
      note: 'Bebês bem descansados geralmente acordam bem-humorados entre 6-7h.'
    },
    {
      id: 2,
      icon: 'Clock',
      color: '#8F7AC5',
      title: 'Rotina de 2 sonecas consolidada',
      mainValue: '3h - 4h',
      instructions: [
        'Primeira janela: 3h - 3h30',
        'Segunda janela: 3h30 - 4h',
        'Última janela (antes de dormir): 4h',
        '2 sonecas por dia: manhã (1h-1h30) e tarde (1h-1h30)',
        'Total de sono diurno: 2-3 horas'
      ],
      note: 'Se o bebê começar a resistir à primeira soneca, pode estar se preparando para 1 soneca (mas isso geralmente acontece após 14-18 meses).'
    },
    {
      id: 3,
      icon: 'Moon',
      color: '#30244F',
      title: 'Rotina noturna estabelecida',
      mainValue: '19:00 - 19:30',
      instructions: [
        'Jantar com a família',
        'Brincadeira calma após jantar',
        'Banho parte da rotina (não precisa ser diário)',
        'Mamada/leite antes de dormir',
        'Livros e canções',
        'Despedida carinhosa e consistente'
      ],
      note: 'Rotina previsível traz segurança para o bebê.'
    },
    {
      id: 4,
      icon: 'Utensils',
      color: '#D98DAE',
      title: 'Alimentação variada',
      mainValue: '3 refeições + 2 lanches',
      meals: [
        '07:00 - Café da manhã + leite',
        '09:30 - Lanche da manhã',
        '12:00 - Almoço completo',
        '15:00 - Lanche da tarde + leite',
        '18:00 - Jantar',
        '19:00 - Leite antes de dormir (se ainda mama)'
      ],
      note: 'A maioria dos bebês dessa idade já pode dormir a noite toda sem precisar mamar.'
    },
    {
      id: 5,
      icon: 'Heart',
      color: '#7BC47F',
      title: 'Lidando com marcos de desenvolvimento',
      mainValue: '',
      instructions: [
        'Praticar andar e outras habilidades durante o dia',
        'Se o bebê ficar de pé no berço, deitar gentilmente sem dar muita atenção',
        'Manter resposta consistente aos despertares',
        'Paciência extra durante saltos de desenvolvimento',
        'Manter rotina mesmo quando o sono regride temporariamente'
      ],
      note: 'A regressão dos 12 meses é temporária. Consistência é a chave!'
    }
  ],
  routine: [
    { time: '06:30', activity: 'Acordar + leite' },
    { time: '07:00', activity: 'Café da manhã' },
    { time: '09:30', activity: '1ª soneca (1h - 1h30)' },
    { time: '11:00', activity: 'Acordar + lanche' },
    { time: '12:30', activity: 'Almoço' },
    { time: '14:00', activity: '2ª soneca (1h - 1h30)' },
    { time: '15:30', activity: 'Acordar + lanche + leite' },
    { time: '18:00', activity: 'Jantar' },
    { time: '18:30', activity: 'Início da rotina noturna' },
    { time: '19:00', activity: 'Leite + rotina' },
    { time: '19:30', activity: 'Dormir' }
  ],
  priorities: {
    'Semana 1-2 - Avaliação': [
      'Avaliar padrão atual de sono',
      'Garantir que o bebê não esteja muito cansado ao final do dia',
      'Verificar se a alimentação está adequada',
      'Manter consistência na rotina'
    ],
    'Semana 3-4 - Ajustes': [
      'Ajustar horários conforme necessário',
      'Trabalhar em qualquer associação de sono pendente',
      'Preparar para transições futuras',
      'Celebrar o progresso!'
    ]
  },
  expectations: [
    { period: 'Semana 1-2', desc: 'Ajustes finos na rotina. Bebê pode testar limites.' },
    { period: 'Semana 3-4', desc: 'Sono consolidado com 2 sonecas e noite completa.' }
  ],
  finalMessage: `Parabéns por todo o caminho percorrido! ${babyName} está crescendo lindamente e vocês formam uma dupla incrível. O primeiro ano é intenso, mas olha quanta coisa conquistaram! Continue confiando nos seus instintos e na consistência. O segundo ano traz novos desafios e alegrias. Estou aqui para ajudar! 💜`
});

// Template para 1-2 anos
const getTemplate13to24 = (babyName, babyAge) => {
  const ageText = babyAge >= 12 ? `${Math.floor(babyAge/12)} ano${babyAge >= 24 ? 's' : ''} e ${babyAge % 12} ${babyAge % 12 === 1 ? 'mês' : 'meses'}` : `${babyAge} meses`;
  return {
    baby_name: babyName,
    baby_age: babyAge,
    summary: `${babyName} tem ${ageText} e está na fase toddler! Essa é uma idade de muita independência, testes de limite e opiniões fortes - inclusive sobre o sono. A transição para 1 soneca geralmente acontece entre 14-18 meses. Os desafios comuns incluem resistência à hora de dormir, chamados constantes, e dificuldade em ficar na cama (especialmente após a transição para cama de solteiro). O foco é manter limites claros com muito amor e consistência.`,
    pillars: [
      {
        id: 1,
        icon: 'Sun',
        color: '#F5A623',
        title: 'Início do dia e rotina matinal',
        mainValue: '06:30 - 07:30',
        instructions: [
          'Acordar natural entre 6:30 e 7:30',
          'Luz especial de "ok para acordar" pode ajudar crianças que acordam muito cedo',
          'Café da manhã em família quando possível',
          'Atividades ao ar livre pela manhã',
          'Gastar energia durante o dia ajuda o sono à noite'
        ],
        note: 'Crianças bem descansadas acordam felizes. Se acorda irritada, pode estar dormindo pouco.'
      },
      {
        id: 2,
        icon: 'Clock',
        color: '#8F7AC5',
        title: 'Uma soneca restauradora',
        mainValue: '5h - 6h de janela',
        instructions: [
          'Transição para 1 soneca: geralmente entre 14-18 meses',
          'Soneca única: 12:30 - 13:00 (após almoço)',
          'Duração ideal: 1h30 - 2h30',
          'Janela da manhã: 5-6 horas após acordar',
          'Janela da tarde: 4-5 horas até dormir à noite',
          'Se a soneca for muito tarde ou longa, pode afetar a noite'
        ],
        note: 'A transição para 1 soneca pode levar 2-4 semanas. Seja paciente!'
      },
      {
        id: 3,
        icon: 'Moon',
        color: '#30244F',
        title: 'Rotina noturna com limites claros',
        mainValue: '19:00 - 20:00',
        instructions: [
          'Aviso de que a hora de dormir está chegando (10 min antes)',
          'Jantar, banho (se for o dia), escovar dentes',
          'Rotina previsível: 2-3 livros, 1-2 canções, orações se fizer parte da rotina',
          'Despedida clara e amorosa',
          'Limites consistentes se houver chamados ou pedidos extras',
          'Use "passes da noite" se a criança sair do quarto repetidamente'
        ],
        note: 'Crianças dessa idade testam limites. Seja firme com amor!'
      },
      {
        id: 4,
        icon: 'Utensils',
        color: '#D98DAE',
        title: 'Alimentação equilibrada',
        mainValue: '3 refeições + 2 lanches',
        meals: [
          '07:30 - Café da manhã completo',
          '10:00 - Lanche da manhã',
          '12:00 - Almoço (antes da soneca)',
          '15:00 - Lanche da tarde (após soneca)',
          '18:30 - Jantar',
          'Leite: 2-3 porções de laticínios por dia é suficiente',
          'Evitar muitos líquidos próximo da hora de dormir se está retirando fralda noturna'
        ],
        note: 'Alimentação adequada durante o dia = menos desculpas para "água" ou "fome" à noite.'
      },
      {
        id: 5,
        icon: 'Heart',
        color: '#7BC47F',
        title: 'Lidando com medos e resistência',
        mainValue: '',
        instructions: [
          'Medos noturnos são comuns nessa idade - valide os sentimentos',
          'Luz de presença pode ajudar (luz vermelha ou laranja)',
          'Quadro de rotina visual ajuda a criança a saber o que esperar',
          'Quadro de recompensas para noites bem dormidas',
          'Evitar ameaças ou punições relacionadas ao sono',
          'Conexão durante o dia reduz resistência à noite'
        ],
        note: 'Crianças que se sentem conectadas e seguras dormem melhor.'
      }
    ],
    routine: [
      { time: '07:00', activity: 'Acordar + café da manhã' },
      { time: '10:00', activity: 'Lanche + atividades' },
      { time: '12:00', activity: 'Almoço' },
      { time: '12:45', activity: 'Rotina da soneca' },
      { time: '13:00', activity: 'Soneca (1h30 - 2h30)' },
      { time: '15:00', activity: 'Acordar + lanche' },
      { time: '18:00', activity: 'Jantar' },
      { time: '18:45', activity: 'Brincadeira calma' },
      { time: '19:15', activity: 'Início da rotina noturna' },
      { time: '19:45', activity: 'Dormir' }
    ],
    priorities: {
      'Semana 1 - Estabelecer expectativas': [
        'Conversar com a criança sobre as regras de sono (de forma simples)',
        'Criar quadro de rotina visual',
        'Estabelecer consequências claras e consistentes',
        'Garantir conexão de qualidade durante o dia'
      ],
      'Semana 2 - Consistência': [
        'Manter rotina mesmo com resistência',
        'Ser firme e amoroso ao mesmo tempo',
        'Não ceder a pedidos extras além do combinado',
        'Celebrar as pequenas vitórias com a criança'
      ],
      'Semana 3-4 - Consolidação': [
        'Avaliar o que está funcionando',
        'Ajustar conforme necessário',
        'Manter quadro de recompensas se estiver ajudando',
        'Preparar para próximos desafios (retirada de fralda noturna, transição para cama)'
      ]
    },
    expectations: [
      { period: 'Semana 1', desc: 'Possível aumento da resistência. A criança vai testar os novos limites.' },
      { period: 'Semana 2', desc: 'Começam as melhorias se houver consistência.' },
      { period: 'Semana 3-4', desc: 'Novos hábitos se estabelecem. Sono mais tranquilo para todos.' }
    ],
    finalMessage: `A fase toddler é desafiadora, mas também muito divertida! ${babyName} está desenvolvendo sua personalidade e autonomia, e isso é lindo de ver. Lembre-se: limites claros são uma forma de amor. Crianças se sentem seguras quando sabem o que esperar. Continue firme, consistente e amorosa. Vocês vão superar essa fase juntas! 💜`
  };
};

// Função principal que seleciona o template baseado na idade
const getTemplateForAge = (babyName, babyAge) => {
  if (babyAge <= 3) {
    return getTemplate0to3(babyName, babyAge);
  } else if (babyAge <= 6) {
    return getTemplate4to6(babyName, babyAge);
  } else if (babyAge <= 9) {
    return getTemplate7to9(babyName, babyAge);
  } else if (babyAge <= 12) {
    return getTemplate10to12(babyName, babyAge);
  } else {
    return getTemplate13to24(babyName, babyAge);
  }
};

// ==================== COMPONENTE PRINCIPAL ====================

const AdminQuickPlanPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Estado inicial
  const [step, setStep] = useState('form'); // 'form', 'edit', 'success'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportId, setReportId] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Dados do formulário inicial
  const [formData, setFormData] = useState({
    babyName: '',
    babyAge: 6,
    motherEmail: '',
    motherWhatsapp: '',
    motherName: '',
    mainConcern: '',
    sleepGoal: '',
    additionalNotes: ''
  });
  
  // Dados do plano
  const [planContent, setPlanContent] = useState(null);
  
  // Faixa etária selecionada
  const [selectedAgeRange, setSelectedAgeRange] = useState('4-6');
  
  // Atualizar faixa etária quando idade muda
  useEffect(() => {
    const newRange = getAgeRangeFromAge(formData.babyAge);
    setSelectedAgeRange(newRange);
  }, [formData.babyAge]);
  
  // Ícones disponíveis
  const iconOptions = [
    { name: 'Sun', icon: Sun },
    { name: 'Moon', icon: Moon },
    { name: 'Clock', icon: Clock },
    { name: 'Heart', icon: Heart },
    { name: 'Utensils', icon: Utensils },
    { name: 'Baby', icon: Baby },
    { name: 'Zap', icon: Zap },
    { name: 'Sparkles', icon: Sparkles },
    { name: 'Star', icon: Star },
    { name: 'Cloud', icon: Cloud }
  ];
  
  const getIconComponent = (iconName) => {
    const found = iconOptions.find(i => i.name === iconName);
    return found ? found.icon : Sparkles;
  };

  // Iniciar edição com template baseado na idade
  const handleStartEdit = () => {
    if (!formData.babyName.trim()) {
      setError('Por favor, informe o nome do bebê');
      return;
    }
    
    const template = getTemplateForAge(formData.babyName, formData.babyAge);
    setPlanContent(template);
    setStep('edit');
    setError(null);
  };

  // Salvar plano (inclui salvar para aprendizado da IA)
  const handleSavePlan = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // 1. Criar o relatório com informações completas
      const newReport = await apiService.createReport({
        baby_name: formData.babyName,
        mother_name: formData.motherName,
        mother_email: formData.motherEmail,
        email: formData.motherEmail,
        whatsapp: formData.motherWhatsapp,
        main_concern: formData.mainConcern,
        sleep_goal: formData.sleepGoal,
        additional_notes: formData.additionalNotes
      });
      
      // 2. Atualizar dados básicos
      await apiService.updateReportBasicInfo(newReport.id, {
        babyName: formData.babyName,
        babyAge: formData.babyAge,
        motherName: formData.motherName,
        mainConcern: formData.mainConcern,
        sleepGoal: formData.sleepGoal,
        additionalNotes: formData.additionalNotes
      });
      
      // 3. Salvar o plano
      await apiService.updateSleepPlan(newReport.id, planContent);
      
      // 4. Aprovar o plano (isso também salva para aprendizado da IA)
      await apiService.approvePlan(newReport.id);
      
      setReportId(newReport.id);
      setStep('success');
    } catch (err) {
      console.error('Erro ao salvar plano:', err);
      setError('Erro ao salvar o plano. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Funções de edição
  const updatePillar = (pillarId, field, value) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => 
        p.id === pillarId ? { ...p, [field]: value } : p
      )
    }));
  };

  const updatePillarInstruction = (pillarId, index, value) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => {
        if (p.id !== pillarId) return p;
        const key = p.instructions ? 'instructions' : 'meals';
        const items = [...(p[key] || [])];
        items[index] = value;
        return { ...p, [key]: items };
      })
    }));
  };

  const addPillarInstruction = (pillarId) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => {
        if (p.id !== pillarId) return p;
        const key = p.instructions ? 'instructions' : 'meals';
        return { ...p, [key]: [...(p[key] || []), ''] };
      })
    }));
  };

  const removePillarInstruction = (pillarId, index) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => {
        if (p.id !== pillarId) return p;
        const key = p.instructions ? 'instructions' : 'meals';
        return { ...p, [key]: p[key].filter((_, i) => i !== index) };
      })
    }));
  };

  const addPillar = () => {
    const newId = Math.max(...planContent.pillars.map(p => p.id)) + 1;
    setPlanContent(prev => ({
      ...prev,
      pillars: [...prev.pillars, {
        id: newId,
        icon: 'Sparkles',
        color: '#8F7AC5',
        title: 'Novo Pilar',
        mainValue: '',
        instructions: [''],
        note: ''
      }]
    }));
  };

  const removePillar = (pillarId) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.filter(p => p.id !== pillarId)
    }));
  };

  // Reordenar pilares
  const movePillarUp = (index) => {
    if (index === 0) return;
    setPlanContent(prev => {
      const newPillars = [...prev.pillars];
      [newPillars[index - 1], newPillars[index]] = [newPillars[index], newPillars[index - 1]];
      return { ...prev, pillars: newPillars };
    });
  };

  const movePillarDown = (index) => {
    if (index === planContent.pillars.length - 1) return;
    setPlanContent(prev => {
      const newPillars = [...prev.pillars];
      [newPillars[index], newPillars[index + 1]] = [newPillars[index + 1], newPillars[index]];
      return { ...prev, pillars: newPillars };
    });
  };

  const updateRoutineItem = (index, field, value) => {
    setPlanContent(prev => ({
      ...prev,
      routine: prev.routine.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addRoutineItem = () => {
    setPlanContent(prev => ({
      ...prev,
      routine: [...prev.routine, { time: '', activity: '' }]
    }));
  };

  const removeRoutineItem = (index) => {
    setPlanContent(prev => ({
      ...prev,
      routine: prev.routine.filter((_, i) => i !== index)
    }));
  };

  // Links
  const getPlanLink = () => `${window.location.origin}/plan/${reportId}`;
  
  const copyLink = () => {
    navigator.clipboard.writeText(getPlanLink());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#FBF4FF]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-[#FAE0D5] p-6 hidden lg:block">
        <div className="mb-8">
          <Link to="/admin/dashboard">
            <Logo variant="v3SonoRotina" size="md" className="cursor-pointer hover:opacity-80 transition-opacity" />
          </Link>
        </div>

        <nav className="space-y-2">
          {[
            { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
            { icon: PlusCircle, label: 'Novo Relatório', path: '/admin/new' },
            { icon: FileText, label: 'Relatórios', path: '/admin/reports' }
          ].map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                location.pathname === item.path
                  ? 'bg-[#8F7AC5] text-white'
                  : 'text-[#6B5B8C] hover:bg-[#FBF4FF]'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#9890A8] hover:text-[#30244F] hover:bg-[#FBF4FF]"
            onClick={() => navigate('/admin')}
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <Link to="/admin/dashboard">
              <Logo variant="v3SonoRotina" size="sm" />
            </Link>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/admin')}
              className="text-[#9890A8]"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* STEP 1: Formulário Inicial */}
          {step === 'form' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-[#F5A623] to-[#D98DAE] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-2xl lg:text-3xl font-bold text-[#30244F]">Plano Rápido</h1>
                <p className="text-[#6B5B8C]">Crie um plano de sono direto, sem precisar do registro de rotina</p>
              </div>

              {/* Form */}
              <div className="bg-white rounded-3xl p-8 shadow-sm space-y-6">
                <div className="bg-[#FBF4FF] rounded-xl p-4 border border-[#FAE0D5]">
                  <p className="text-sm text-[#6B5B8C]">
                    <strong className="text-[#8F7AC5]">💡 Ideal para:</strong> Mães que já preencheram a rotina em outro formato 
                    (papel, WhatsApp, etc.) e você já tem as informações necessárias.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-[#30244F]">Nome do bebê *</Label>
                    <Input
                      placeholder="Ex: Helena"
                      value={formData.babyName}
                      onChange={(e) => setFormData({ ...formData, babyName: e.target.value })}
                      className="border-[#FAE0D5] focus:border-[#8F7AC5] mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Idade (meses)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="36"
                      placeholder="6"
                      value={formData.babyAge}
                      onChange={(e) => setFormData({ ...formData, babyAge: parseInt(e.target.value) || 6 })}
                      className="border-[#FAE0D5] focus:border-[#8F7AC5] mt-1"
                    />
                  </div>
                </div>
                
                {/* Indicador de faixa etária */}
                <div className="bg-gradient-to-r from-[#8F7AC5]/10 to-[#D98DAE]/10 rounded-xl p-4 border border-[#FAE0D5]">
                  <Label className="text-[#30244F] text-sm">Template selecionado automaticamente:</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {AGE_RANGES.map(range => (
                      <span
                        key={range.id}
                        className={`px-3 py-1 rounded-full text-sm transition-all ${
                          selectedAgeRange === range.id
                            ? 'bg-[#8F7AC5] text-white font-medium'
                            : 'bg-white text-[#9890A8] border border-[#FAE0D5]'
                        }`}
                      >
                        {range.label}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-[#9890A8] mt-2">
                    O template será personalizado com janelas de sono, rotinas e orientações específicas para a faixa etária.
                  </p>
                </div>

                <div>
                  <Label className="text-[#30244F]">E-mail da mãe</Label>
                  <p className="text-xs text-[#9890A8] mb-1">Para ela acessar o plano depois</p>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9890A8]" />
                    <Input
                      type="email"
                      placeholder="email@exemplo.com"
                      value={formData.motherEmail}
                      onChange={(e) => setFormData({ ...formData, motherEmail: e.target.value })}
                      className="pl-10 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#30244F]">WhatsApp da mãe</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9890A8]" />
                    <Input
                      type="tel"
                      placeholder="(11) 99999-9999"
                      value={formData.motherWhatsapp}
                      onChange={(e) => setFormData({ ...formData, motherWhatsapp: e.target.value })}
                      className="pl-10 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>
                </div>

                {/* Seção de informações adicionais para consulta futura */}
                <div className="border-t border-[#FAE0D5] pt-6 mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-[#8F7AC5]" />
                    <h3 className="font-semibold text-[#30244F]">Informações para consulta futura</h3>
                    <span className="text-xs text-[#9890A8]">(opcional)</span>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-[#30244F]">Nome da mãe</Label>
                      <Input
                        placeholder="Ex: Mariana"
                        value={formData.motherName}
                        onChange={(e) => setFormData({ ...formData, motherName: e.target.value })}
                        className="border-[#FAE0D5] focus:border-[#8F7AC5] mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-[#30244F]">Principal queixa/preocupação</Label>
                      <Textarea
                        placeholder="Ex: Bebê acorda muitas vezes à noite, dificuldade para fazer sonecas..."
                        value={formData.mainConcern}
                        onChange={(e) => setFormData({ ...formData, mainConcern: e.target.value })}
                        className="border-[#FAE0D5] focus:border-[#8F7AC5] mt-1 min-h-[80px]"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-[#30244F]">Objetivo do sono</Label>
                      <Textarea
                        placeholder="Ex: Fazer o bebê dormir a noite toda, estabelecer rotina de sonecas..."
                        value={formData.sleepGoal}
                        onChange={(e) => setFormData({ ...formData, sleepGoal: e.target.value })}
                        className="border-[#FAE0D5] focus:border-[#8F7AC5] mt-1 min-h-[80px]"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-[#30244F]">Observações adicionais</Label>
                      <Textarea
                        placeholder="Qualquer informação relevante sobre o bebê, família, rotina atual..."
                        value={formData.additionalNotes}
                        onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                        className="border-[#FAE0D5] focus:border-[#8F7AC5] mt-1 min-h-[80px]"
                      />
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <div className="flex gap-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin/new')}
                    className="flex-1 border-[#FAE0D5]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar
                  </Button>
                  <Button
                    onClick={handleStartEdit}
                    className="flex-1 bg-[#F5A623] hover:bg-[#D98830] py-6 text-lg"
                  >
                    <Zap className="w-5 h-5 mr-2" />
                    Criar Template
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 2: Edição do Plano */}
          {step === 'edit' && planContent && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Header */}
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-[#30244F]">
                    Plano de {formData.babyName}
                  </h1>
                  <p className="text-[#6B5B8C]">{formData.babyAge} {formData.babyAge === 1 ? 'mês' : 'meses'} • Template {AGE_RANGES.find(r => r.id === selectedAgeRange)?.label}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep('form')}
                    className="border-[#FAE0D5]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Voltar
                  </Button>
                  <Button
                    onClick={handleSavePlan}
                    disabled={loading}
                    className="bg-[#7BC47F] hover:bg-[#5BA35F]"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-1" />
                    )}
                    Salvar e Aprovar
                  </Button>
                </div>
              </div>
              
              {/* Info sobre aprendizado */}
              <div className="bg-gradient-to-r from-[#7BC47F]/10 to-[#5BA35F]/10 rounded-xl p-4 border border-[#7BC47F]/30">
                <p className="text-sm text-[#5BA35F]">
                  <strong>🧠 Aprendizado IA:</strong> Ao salvar e aprovar, este plano será usado para melhorar as sugestões futuras da IA, 
                  personalizando cada vez mais ao seu estilo de trabalho.
                </p>
              </div>

              {/* Análise */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <Label className="text-[#30244F] font-semibold mb-2 block">Análise da Situação</Label>
                <Textarea
                  value={planContent.summary}
                  onChange={(e) => setPlanContent({ ...planContent, summary: e.target.value })}
                  className="min-h-[120px] border-[#FAE0D5]"
                />
              </div>

              {/* Pilares */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-[#30244F]">Pilares do Plano</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addPillar}
                    className="border-[#8F7AC5] text-[#8F7AC5]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Pilar
                  </Button>
                </div>

                {planContent.pillars.map((pillar, pillarIndex) => {
                  const IconComponent = getIconComponent(pillar.icon);
                  const items = pillar.instructions || pillar.meals || [];
                  
                  return (
                    <div key={pillar.id} className="bg-white rounded-2xl p-6 shadow-sm">
                      <div className="flex items-start gap-4 mb-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${pillar.color}20` }}
                        >
                          <IconComponent className="w-6 h-6" style={{ color: pillar.color }} />
                        </div>
                        <div className="flex-1 space-y-3">
                          <div className="flex flex-wrap gap-2 items-center">
                            {/* Botões de reordenação */}
                            <div className="flex flex-col gap-0.5 mr-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => movePillarUp(pillarIndex)}
                                disabled={pillarIndex === 0}
                                className="text-[#8F7AC5] hover:bg-[#8F7AC5]/10 p-0 h-5 w-5"
                                title="Mover para cima"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => movePillarDown(pillarIndex)}
                                disabled={pillarIndex === planContent.pillars.length - 1}
                                className="text-[#8F7AC5] hover:bg-[#8F7AC5]/10 p-0 h-5 w-5"
                                title="Mover para baixo"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                            </div>
                            <Input
                              value={pillar.title}
                              onChange={(e) => updatePillar(pillar.id, 'title', e.target.value)}
                              className="flex-1 min-w-[200px] border-[#FAE0D5] font-semibold"
                              placeholder="Título do pilar"
                            />
                            <Input
                              value={pillar.mainValue || ''}
                              onChange={(e) => updatePillar(pillar.id, 'mainValue', e.target.value)}
                              className="w-32 border-[#FAE0D5]"
                              placeholder="Valor"
                            />
                            <select
                              value={pillar.icon}
                              onChange={(e) => updatePillar(pillar.id, 'icon', e.target.value)}
                              className="w-24 border border-[#FAE0D5] rounded-md px-2 text-sm"
                            >
                              {iconOptions.map(opt => (
                                <option key={opt.name} value={opt.name}>{opt.name}</option>
                              ))}
                            </select>
                            <input
                              type="color"
                              value={pillar.color}
                              onChange={(e) => updatePillar(pillar.id, 'color', e.target.value)}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removePillar(pillar.id)}
                              className="text-red-500 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Instruções/Meals */}
                          <div className="space-y-2 pl-4 border-l-2 border-[#FAE0D5]">
                            {items.map((item, idx) => (
                              <div key={idx} className="flex gap-2">
                                <Input
                                  value={item}
                                  onChange={(e) => updatePillarInstruction(pillar.id, idx, e.target.value)}
                                  className="flex-1 border-[#FAE0D5] text-sm"
                                  placeholder="Instrução..."
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removePillarInstruction(pillar.id, idx)}
                                  className="text-red-400 hover:bg-red-50"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => addPillarInstruction(pillar.id)}
                              className="text-[#8F7AC5] text-xs"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Adicionar item
                            </Button>
                          </div>

                          {/* Nota */}
                          <Input
                            value={pillar.note || ''}
                            onChange={(e) => updatePillar(pillar.id, 'note', e.target.value)}
                            className="border-[#FAE0D5] text-sm italic"
                            placeholder="Nota adicional (opcional)..."
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Rotina */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#30244F]">Rotina Sugerida</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addRoutineItem}
                    className="border-[#8F7AC5] text-[#8F7AC5]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                <div className="space-y-2">
                  {planContent.routine.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input
                        value={item.time}
                        onChange={(e) => updateRoutineItem(idx, 'time', e.target.value)}
                        className="w-24 border-[#FAE0D5] text-center font-mono"
                        placeholder="00:00"
                      />
                      <Input
                        value={item.activity}
                        onChange={(e) => updateRoutineItem(idx, 'activity', e.target.value)}
                        className="flex-1 border-[#FAE0D5]"
                        placeholder="Atividade..."
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRoutineItem(idx)}
                        className="text-red-400 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mensagem Final */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <Label className="text-[#30244F] font-semibold mb-2 block">Mensagem Final</Label>
                <Textarea
                  value={planContent.finalMessage}
                  onChange={(e) => setPlanContent({ ...planContent, finalMessage: e.target.value })}
                  className="min-h-[100px] border-[#FAE0D5]"
                />
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                  {error}
                </div>
              )}

              {/* Botão Salvar fixo */}
              <div className="sticky bottom-4 flex justify-end">
                <Button
                  onClick={handleSavePlan}
                  disabled={loading}
                  className="bg-[#7BC47F] hover:bg-[#5BA35F] shadow-lg px-8 py-6"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-5 h-5 mr-2" />
                  )}
                  Salvar e Aprovar Plano
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Sucesso */}
          {step === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-[#30244F] mb-2">Plano Criado e Aprovado! 🎉</h2>
              <p className="text-[#6B5B8C] mb-2">
                O plano de sono de {formData.babyName} está pronto para ser compartilhado
              </p>
              <p className="text-sm text-[#7BC47F] mb-8">
                ✓ Este plano foi salvo na base de aprendizado da IA
              </p>

              {/* Link Card */}
              <div className="bg-white rounded-3xl p-6 shadow-sm mb-6 max-w-lg mx-auto">
                <Label className="text-[#30244F] block text-left mb-2">Link do Plano</Label>
                <div className="flex gap-2">
                  <Input
                    value={getPlanLink()}
                    readOnly
                    className="bg-[#FBF4FF] border-[#FAE0D5]"
                  />
                  <Button
                    onClick={copyLink}
                    className={copied ? 'bg-green-600' : 'bg-[#8F7AC5] hover:bg-[#30244F]'}
                  >
                    {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={() => window.open(getPlanLink(), '_blank')}
                  className="border-[#8F7AC5] text-[#8F7AC5]"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Ver Plano
                </Button>
                
                {formData.motherWhatsapp && (
                  <Button
                    variant="outline"
                    className="border-green-500 text-green-600 hover:bg-green-50"
                    onClick={() => {
                      const phone = formData.motherWhatsapp.replace(/\D/g, '');
                      const message = encodeURIComponent(`Olá! 💜\n\nO plano de sono do(a) ${formData.babyName} está pronto!\n\nAcesse aqui: ${getPlanLink()}\n\nQualquer dúvida, estou aqui!`);
                      window.open(`https://wa.me/55${phone}?text=${message}`, '_blank');
                    }}
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Enviar pelo WhatsApp
                  </Button>
                )}

                <Button
                  onClick={() => navigate(`/admin/reports/${reportId}/plan`)}
                  className="bg-[#8F7AC5] hover:bg-[#30244F]"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Editar no Painel
                </Button>
              </div>

              <div className="mt-8">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setStep('form');
                    setFormData({ babyName: '', babyAge: 6, motherEmail: '', motherWhatsapp: '' });
                    setPlanContent(null);
                    setReportId('');
                  }}
                  className="text-[#6B5B8C]"
                >
                  Criar outro plano rápido
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminQuickPlanPage;
